/**
 * CliLookupObjectsDialog.jsx
 *
 * Dialog for configuring which Salesforce objects (Lead, Contact, Account)
 * are searched when matching an incoming caller's phone number (Caller Number lookup).
 *
 * The order of selected items represents lookup priority — objects higher
 * in the list are searched first.
 *
 * Props:
 *   open            {boolean}    - Controlled open state
 *   selectedObjects {string[]}   - Currently saved object IDs e.g. ["Lead","Contact"]
 *   onSave          {function}   - Called with string[] of saved object IDs on confirm
 *   onCancel        {function}   - Called when the dialog is dismissed without saving
 */

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Text,
    Link,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import OrderedMultiSelectPickList from "../OrderedMultiSelectPickList";

// ── Fixed set of Caller Number lookup object options (order = default priority) ────
const CLI_OBJECTS = [
    { id: "Contact", label: "Contact" },
    { id: "Lead",    label: "Lead" },
    { id: "Account", label: "Account" },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    content: {
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        paddingTop: "4px",
    },

    description: {
        fontSize: "13px",
        color: tokens.colorNeutralForeground2,
        lineHeight: "1.5",
    },
});

// ── Component ─────────────────────────────────────────────────────────────────

export default function CliLookupObjectsDialog({
    open,
    selectedObjects = [], // [] = nothing selected; [...] = current saved config
    onSave,
    onCancel,
}) {
    const styles = useStyles();

    // Local editing state — initialised / re-synced whenever the dialog opens
    const [localObjects, setLocalObjects] = useState([]);

    useEffect(() => {
        if (open) {
            // null means never configured → start with nothing selected (shows "None configured" in the table).
            // [] means explicitly cleared → also nothing selected.
            // [...] means saved config → restore it.
            const source = selectedObjects ?? [];
            setLocalObjects(
                source
                    .map((id) => CLI_OBJECTS.find((o) => o.id === id))
                    .filter(Boolean),
            );
        }
    }, [open, selectedObjects]);

    const handleSave = () => {
        onSave?.(localObjects.map((o) => o.id));
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(_, data) => {
                if (!data.open) onCancel?.();
            }}
        >
            <DialogSurface style={{ width: "640px", maxWidth: "96vw" }}>
                <DialogBody>
                    <DialogTitle>Record Linking Configuration</DialogTitle>

                    <DialogContent>
                        <div className={styles.content}>
                            <Text className={styles.description}>
                                Select which Salesforce objects are searched for records
                                matching the caller&apos;s phone number. Order determines
                                lookup priority. For CTI calls, matched records populate
                                the <strong>Name</strong> (WhoId) and{" "}
                                <strong>Related To</strong> (WhatId) fields on the Task.
                                For SCV calls, the highest-priority matched record
                                populates the <strong>Related To</strong> (RelatedRecordId)
                                field on the VoiceCall.
                            </Text>

                            <OrderedMultiSelectPickList
                                availableItems={CLI_OBJECTS}
                                selectedItems={localObjects}
                                getItemId={(item) => item.id}
                                getItemLabel={(item) => item.label}
                                onSelectionChange={setLocalObjects}
                                searchable={false}
                                height={200}
                                showFinalOrder={false}
                            />

                            {localObjects.length === 0 && (
                                <MessageBar intent="warning">
                                    <MessageBarBody>
                                        <MessageBarTitle>No objects selected</MessageBarTitle>
                                        Without any object selection, the <strong>Name</strong> and{" "}
                                        <strong>Related To</strong> fields will not be populated on
                                        call, and <strong>Matched Record Assignment</strong>{" "}
                                        will be disabled across all queues regardless of their current
                                        setting. Select at least one object to enable Caller Number lookup.
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            {/* Grouped informational tips — always visible so admins know the runtime rules */}
                            <MessageBar intent="info" layout="multiline">
                                <MessageBarBody>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                        <span>
                                            When a record is matched to a caller&apos;s number, the call
                                            record ownership can also be assigned to the owner of the
                                            matched record. Visit the{" "}
                                            <Link
                                                href="/admin?page=manageQueue"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Queue Screen
                                            </Link>{" "}
                                            to configure call record ownership.
                                        </span>
                                        <span>
                                            Where multiple records are matched from a single object,
                                            we reflect Salesforce&apos;s prioritisation of the most
                                            relevant record.
                                        </span>
                                    </div>
                                </MessageBarBody>
                            </MessageBar>
                        </div>
                    </DialogContent>

                    <DialogActions>
                        <Button appearance="secondary" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button appearance="primary" onClick={handleSave}>
                            Save
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}

// ── PropTypes ─────────────────────────────────────────────────────────────────

CliLookupObjectsDialog.propTypes = {
    /** Controls whether the dialog is visible */
    open: PropTypes.bool.isRequired,
    /** Currently saved Caller Number lookup object IDs, or null if never configured */
    selectedObjects: PropTypes.arrayOf(PropTypes.string),
    /** Called with the new ordered string[] of object IDs when the admin saves */
    onSave: PropTypes.func,
    /** Called when the dialog is closed without saving */
    onCancel: PropTypes.func,
};
